import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { ValueRange, FieldCondition } from './ValidationRuleBuilder';

interface ValueRangeBuilderProps {
  ranges: ValueRange[];
  onChange: (ranges: ValueRange[]) => void;
  availableFields?: Array<{ id: number; label: string; name: string; field_type: string; options?: string; allow_multiple?: boolean }>;
  validationFieldId?: number;
}

const ValueRangeBuilder: React.FC<ValueRangeBuilderProps> = ({ ranges, onChange, availableFields = [], validationFieldId }) => {
  const [localRanges, setLocalRanges] = useState<ValueRange[]>(ranges || []);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<Partial<ValueRange>>({
    min: undefined,
    max: undefined,
    validation: {
      type: 'min_date_days',
      value: 0,
      message: ''
    }
  });

  useEffect(() => {
    setLocalRanges(ranges || []);
  }, [ranges]);

  const addRange = () => {
    if (!formData.validation || !formData.validation.value) {
      return;
    }

    const newRange: ValueRange = {
      min: formData.min !== undefined && formData.min !== null && !isNaN(Number(formData.min)) ? Number(formData.min) : undefined,
      max: formData.max !== undefined && formData.max !== null && !isNaN(Number(formData.max)) ? Number(formData.max) : undefined,
      validation: {
        type: formData.validation.type || 'min_date_days',
        value: formData.validation.value,
        message: formData.validation.message || undefined
      },
      conditions: formData.conditions && formData.conditions.length > 0 ? formData.conditions : undefined
    };

    const updatedRanges = editingIndex !== null
      ? localRanges.map((r, i) => i === editingIndex ? newRange : r)
      : [...localRanges, newRange];

    // Ordenar faixas por valor mínimo
    updatedRanges.sort((a, b) => {
      const minA = a.min ?? -Infinity;
      const minB = b.min ?? -Infinity;
      return minA - minB;
    });

    setLocalRanges(updatedRanges);
    onChange(updatedRanges);
    resetForm();
  };

  const removeRange = (index: number) => {
    const updatedRanges = localRanges.filter((_, i) => i !== index);
    setLocalRanges(updatedRanges);
    onChange(updatedRanges);
  };

  const editRange = (index: number) => {
    const range = localRanges[index];
    setFormData({
      min: range.min,
      max: range.max,
      validation: range.validation,
      conditions: range.conditions
    });
    setEditingIndex(index);
  };

  const resetForm = () => {
    setFormData({
      min: undefined,
      max: undefined,
      validation: {
        type: 'min_date_days',
        value: 0,
        message: ''
      },
      conditions: undefined
    });
    setEditingIndex(null);
  };

  const validateRanges = (): { hasGaps: boolean; hasOverlaps: boolean; warnings: string[] } => {
    const warnings: string[] = [];
    let hasGaps = false;
    let hasOverlaps = false;

    if (localRanges.length < 2) {
      return { hasGaps: false, hasOverlaps: false, warnings: [] };
    }

    const sortedRanges = [...localRanges].sort((a, b) => {
      const minA = a.min ?? -Infinity;
      const minB = b.min ?? -Infinity;
      return minA - minB;
    });

    // Tolerância para considerar fronteiras adjacentes (ex: 3000 e 3000.01)
    const EPSILON = 0.01;

    // Verificar gaps e sobreposições
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const current = sortedRanges[i];
      const next = sortedRanges[i + 1];

      const currentMax = current.max ?? Infinity;
      const nextMin = next.min ?? -Infinity;

      // Se ambos são finitos, verificar se há gap ou sobreposição
      if (currentMax !== Infinity && nextMin !== -Infinity) {
        const diff = nextMin - currentMax;
        
        // Se a diferença é maior que a tolerância, há um gap real
        if (diff > EPSILON) {
          hasGaps = true;
          warnings.push(`Gap entre faixa ${i + 1} (até ${currentMax}) e faixa ${i + 2} (de ${nextMin})`);
        } 
        // Se a diferença é negativa (ou seja, há sobreposição), avisar
        else if (diff < -EPSILON) {
          hasOverlaps = true;
          warnings.push(`Sobreposição entre faixa ${i + 1} e faixa ${i + 2}`);
        }
        // Se a diferença está dentro da tolerância (0 a EPSILON), considera-se fronteira adjacente válida
      } else if (currentMax === Infinity && nextMin !== -Infinity) {
        // Faixa atual vai até infinito, próxima começa em valor finito - não há gap
      } else if (currentMax !== Infinity && nextMin === -Infinity) {
        // Faixa atual termina em valor finito, próxima começa em -infinito - não há gap
      }
    }

    return { hasGaps, hasOverlaps, warnings };
  };

  const { hasGaps, hasOverlaps, warnings } = validateRanges();

  const formatRangeDisplay = (range: ValueRange): string => {
    const min = range.min !== undefined ? range.min.toLocaleString('pt-BR') : 'menos infinito';
    const max = range.max !== undefined ? range.max.toLocaleString('pt-BR') : 'mais infinito';
    
    if (range.min === undefined && range.max !== undefined) {
      return `Até ${max}`;
    } else if (range.min !== undefined && range.max === undefined) {
      return `De ${min} em diante`;
    } else if (range.min === undefined && range.max === undefined) {
      return 'Todos os valores';
    } else {
      return `De ${min} até ${max}`;
    }
  };

  const getValidationTypeLabel = (type: string): string => {
    const labels: { [key: string]: string } = {
      'min_date_days': 'Dias Mínimos (Data)',
      'min_value': 'Valor Mínimo',
      'max_value': 'Valor Máximo',
      'equals': 'Igual a',
      'custom': 'Personalizado'
    };
    return labels[type] || type;
  };

  return (
    <div>
      {/* Avisos sobre gaps e sobreposições */}
      {warnings.length > 0 && (
        <Alert variant="warning" className="mb-3">
          <Alert.Heading>Avisos sobre as faixas</Alert.Heading>
          <ul className="mb-0">
            {warnings.map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Lista de faixas existentes */}
      {localRanges.length > 0 && (
        <div className="mb-3">
          <h6 className="small mb-2">Faixas Configuradas:</h6>
          {localRanges.map((range, index) => (
            <div key={index} className="border rounded p-2 mb-2">
              <Row className="align-items-center">
                <Col md={4}>
                  <strong>Faixa:</strong> {formatRangeDisplay(range)}
                </Col>
                <Col md={4}>
                  <strong>Validação:</strong> {getValidationTypeLabel(range.validation.type)} = {range.validation.value}
                  {range.validation.message && (
                    <div className="text-muted small">"{range.validation.message}"</div>
                  )}
                </Col>
                <Col md={4} className="text-end">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2"
                    onClick={() => editRange(index)}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeRange(index)}
                  >
                    Remover
                  </Button>
                </Col>
              </Row>
            </div>
          ))}
        </div>
      )}

      {/* Formulário para adicionar/editar faixa */}
      <div className="border rounded p-3 mb-3">
        <h6 className="mb-3">{editingIndex !== null ? 'Editar Faixa' : 'Adicionar Nova Faixa'}</h6>
        <Row className="g-2">
          <Col md={3}>
            <Form.Label className="small">Valor Mínimo (opcional)</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              step="0.01"
              value={formData.min !== undefined ? formData.min : ''}
              onChange={(e) => setFormData({ ...formData, min: e.target.value ? parseFloat(e.target.value) : undefined })}
              placeholder="Ex: 3001"
            />
            <Form.Text className="text-muted small">Deixe vazio para -Infinity</Form.Text>
          </Col>
          <Col md={3}>
            <Form.Label className="small">Valor Máximo (opcional)</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              step="0.01"
              value={formData.max !== undefined ? formData.max : ''}
              onChange={(e) => setFormData({ ...formData, max: e.target.value ? parseFloat(e.target.value) : undefined })}
              placeholder="Ex: 30000"
            />
            <Form.Text className="text-muted small">Deixe vazio para Infinity</Form.Text>
          </Col>
          <Col md={2}>
            <Form.Label className="small">Tipo de Validação</Form.Label>
            <Form.Select
              size="sm"
              value={formData.validation?.type || 'min_date_days'}
              onChange={(e) => setFormData({
                ...formData,
                validation: {
                  ...formData.validation!,
                  type: e.target.value as any
                }
              })}
            >
              <option value="min_date_days">Dias Mínimos (Data)</option>
              <option value="min_value">Valor Mínimo</option>
              <option value="max_value">Valor Máximo</option>
              <option value="equals">Igual a</option>
              <option value="custom">Personalizado</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label className="small">Valor da Validação</Form.Label>
            <Form.Control
              type={formData.validation?.type === 'min_date_days' || formData.validation?.type === 'min_value' || formData.validation?.type === 'max_value' ? 'number' : 'text'}
              size="sm"
              step={formData.validation?.type === 'min_date_days' ? '1' : '0.01'}
              value={formData.validation?.value || ''}
              onChange={(e) => setFormData({
                ...formData,
                validation: {
                  ...formData.validation!,
                  value: formData.validation?.type === 'min_date_days' || formData.validation?.type === 'min_value' || formData.validation?.type === 'max_value'
                    ? (e.target.value ? parseFloat(e.target.value) : 0)
                    : e.target.value
                }
              })}
              placeholder={formData.validation?.type === 'min_date_days' ? 'Ex: 26' : 'Valor'}
            />
          </Col>
          <Col md={2} className="d-flex align-items-end">
            <Button
              variant={editingIndex !== null ? 'success' : 'primary'}
              size="sm"
              onClick={addRange}
              className="w-100"
            >
              {editingIndex !== null ? 'Salvar' : '+ Adicionar'}
            </Button>
            {editingIndex !== null && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={resetForm}
                className="ms-2"
              >
                Cancelar
              </Button>
            )}
          </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <Form.Label className="small">Mensagem de Erro Personalizada (opcional)</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              value={formData.validation?.message || ''}
              onChange={(e) => setFormData({
                ...formData,
                validation: {
                  ...formData.validation!,
                  message: e.target.value || undefined
                }
              })}
              placeholder="Ex: Para valores até R$ 3.000, o prazo mínimo é de 26 dias"
            />
          </Col>
        </Row>

        {/* Seção de Condições Adicionais para esta faixa */}
        {availableFields && availableFields.length > 0 && (
          <div className="mt-3">
            <Form.Check
              type="checkbox"
              label="Adicionar Condições Adicionais para esta faixa"
              checked={(formData.conditions && formData.conditions.length > 0) || false}
              onChange={(e) => {
                if (e.target.checked && !formData.conditions) {
                  setFormData({ ...formData, conditions: [] });
                } else if (!e.target.checked) {
                  setFormData({ ...formData, conditions: undefined });
                }
              }}
              className="mb-2"
            />
            
            {formData.conditions && formData.conditions.length >= 0 && (
              <div className="border rounded p-3">
                <h6 className="small mb-2">Condições Adicionais desta Faixa:</h6>
                {formData.conditions.map((condition, condIndex) => {
                  const conditionField = availableFields.find(f => f.id === condition.field_id);
                  const isMultiCheckbox = conditionField?.field_type === 'check' && conditionField?.allow_multiple;
                  
                  // Parsear opções do checkbox múltiplo
                  let checkboxOptions: string[] = [];
                  if (isMultiCheckbox && conditionField?.options) {
                    try {
                      const parsed = JSON.parse(conditionField.options);
                      if (Array.isArray(parsed)) {
                        checkboxOptions = parsed;
                      }
                    } catch {
                      checkboxOptions = conditionField.options
                        .split(/[,\n]/)
                        .map(opt => opt.trim())
                        .filter(opt => opt.length > 0);
                    }
                  }
                  
                  return (
                    <div key={condIndex} className="border rounded p-2 mb-2">
                      <Row className="g-2 align-items-center">
                        <Col md={3}>
                          <Form.Label className="small">Campo</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.field_id || ''}
                            onChange={(e) => {
                              const newConditions = [...formData.conditions!];
                              newConditions[condIndex] = {
                                ...condition,
                                field_id: parseInt(e.target.value),
                                value: undefined
                              };
                              setFormData({ ...formData, conditions: newConditions });
                            }}
                          >
                            <option value="">Selecione...</option>
                            {availableFields
                              .filter(f => f.id !== validationFieldId)
                              .map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.label} ({f.name})
                                </option>
                              ))}
                          </Form.Select>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small">Operador</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.operator || 'equals'}
                            onChange={(e) => {
                              const newConditions = [...formData.conditions!];
                              newConditions[condIndex] = {
                                ...condition,
                                operator: e.target.value as any,
                                value: (e.target.value === 'checked' || e.target.value === 'unchecked') ? undefined : condition.value
                              };
                              setFormData({ ...formData, conditions: newConditions });
                            }}
                          >
                            {isMultiCheckbox ? (
                              <>
                                <option value="checked">Marcado (qualquer opção)</option>
                                <option value="unchecked">Desmarcado (nenhuma opção)</option>
                                <option value="equals">Igual a (opção específica)</option>
                                <option value="not_equals">Diferente de (opção específica)</option>
                              </>
                            ) : (
                              <>
                                <option value="checked">Marcado</option>
                                <option value="unchecked">Desmarcado</option>
                                <option value="equals">Igual a</option>
                                <option value="not_equals">Diferente de</option>
                              </>
                            )}
                          </Form.Select>
                        </Col>
                        {(condition.operator === 'equals' || condition.operator === 'not_equals') && (
                          <Col md={2}>
                            <Form.Label className="small">Valor</Form.Label>
                            {isMultiCheckbox && checkboxOptions.length > 0 ? (
                              <Form.Select
                                size="sm"
                                value={condition.value || ''}
                                onChange={(e) => {
                                  const newConditions = [...formData.conditions!];
                                  newConditions[condIndex] = {
                                    ...condition,
                                    value: e.target.value
                                  };
                                  setFormData({ ...formData, conditions: newConditions });
                                }}
                              >
                                <option value="">Selecione a opção...</option>
                                {checkboxOptions.map((opt, idx) => (
                                  <option key={idx} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </Form.Select>
                            ) : (
                              <Form.Control
                                type="text"
                                size="sm"
                                value={condition.value || ''}
                                onChange={(e) => {
                                  const newConditions = [...formData.conditions!];
                                  newConditions[condIndex] = {
                                    ...condition,
                                    value: e.target.value
                                  };
                                  setFormData({ ...formData, conditions: newConditions });
                                }}
                                placeholder={isMultiCheckbox ? "Digite o nome da opção" : "Valor esperado"}
                              />
                            )}
                          </Col>
                        )}
                        <Col md={2}>
                          <Form.Label className="small">Tipo Override</Form.Label>
                          <Form.Select
                            size="sm"
                            value={condition.override_validation?.type || 'min_date_days'}
                            onChange={(e) => {
                              const newConditions = [...formData.conditions!];
                              newConditions[condIndex] = {
                                ...condition,
                                override_validation: {
                                  ...condition.override_validation,
                                  type: e.target.value as any,
                                  value: condition.override_validation?.value || 0
                                } as any
                              };
                              setFormData({ ...formData, conditions: newConditions });
                            }}
                          >
                            <option value="min_date_days">Dias Mínimos</option>
                            <option value="min_value">Valor Mínimo</option>
                            <option value="max_value">Valor Máximo</option>
                            <option value="equals">Igual a</option>
                          </Form.Select>
                        </Col>
                        <Col md={2}>
                          <Form.Label className="small">Valor Override</Form.Label>
                          <Form.Control
                            type={condition.override_validation?.type === 'min_date_days' || condition.override_validation?.type === 'min_value' || condition.override_validation?.type === 'max_value' ? 'number' : 'text'}
                            size="sm"
                            step={condition.override_validation?.type === 'min_date_days' ? '1' : '0.01'}
                            value={condition.override_validation?.value || ''}
                            onChange={(e) => {
                              const newConditions = [...formData.conditions!];
                              newConditions[condIndex] = {
                                ...condition,
                                override_validation: {
                                  ...condition.override_validation!,
                                  value: condition.override_validation?.type === 'min_date_days' || condition.override_validation?.type === 'min_value' || condition.override_validation?.type === 'max_value'
                                    ? (e.target.value ? parseFloat(e.target.value) : 0)
                                    : e.target.value
                                } as any
                              };
                              setFormData({ ...formData, conditions: newConditions });
                            }}
                            placeholder="Valor"
                          />
                        </Col>
                        <Col md={1} className="d-flex align-items-end">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              const newConditions = formData.conditions!.filter((_, i) => i !== condIndex);
                              setFormData({ ...formData, conditions: newConditions.length > 0 ? newConditions : undefined });
                            }}
                            className="w-100"
                          >
                            ×
                          </Button>
                        </Col>
                      </Row>
                      <Row className="g-2 mt-1">
                        <Col md={12}>
                          <Form.Label className="small">Mensagem de erro quando o override se aplica (opcional)</Form.Label>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={condition.override_validation?.message || ''}
                            onChange={(e) => {
                              const newConditions = [...formData.conditions!];
                              const msg = e.target.value.trim();
                              newConditions[condIndex] = {
                                ...condition,
                                override_validation: {
                                  ...condition.override_validation!,
                                  message: msg || undefined
                                } as any
                              };
                              setFormData({ ...formData, conditions: newConditions });
                            }}
                            placeholder="Ex.: Com minuta padrão, o prazo mínimo nesta faixa é de 30 dias."
                          />
                        </Col>
                      </Row>
                    </div>
                  );
                })}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    const newConditions = [...(formData.conditions || []), {
                      field_id: 0,
                      operator: 'checked' as const,
                      override_validation: {
                        type: 'min_date_days' as const,
                        value: 0
                      }
                    }];
                    setFormData({ ...formData, conditions: newConditions });
                  }}
                >
                  + Adicionar Condição
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ValueRangeBuilder;
